import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, query, orderBy, onSnapshot, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';

const AdminDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('calendar');
  const [events, setEvents] = useState([]);
  const [services, setServices] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [newService, setNewService] = useState({ name: '', description: '', duration: '', trainer: '' });
  const [showEventModal, setShowEventModal] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: '', start: '', end: '', description: '', trainer: '' });
  const [orders, setOrders] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    try {
      // Load calendar events
      const eventsQuery = query(collection(db, 'events'), orderBy('start', 'asc'));
      const eventsUnsubscribe = onSnapshot(eventsQuery, 
        (snapshot) => {
          const eventsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setEvents(eventsData);
          setLoading(false);
        },
        (error) => {
          console.error('Error loading events:', error);
          setError('Unable to load events. Please check Firestore rules.');
          setLoading(false);
        }
      );

      // Load services
      const servicesQuery = query(collection(db, 'services'), orderBy('createdAt', 'desc'));
      const servicesUnsubscribe = onSnapshot(servicesQuery, 
        (snapshot) => {
          const servicesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setServices(servicesData);
        },
        (error) => {
          console.error('Error loading services:', error);
        }
      );

      // Load messages
      const messagesQuery = query(collection(db, 'messages'), orderBy('createdAt', 'desc'));
      const messagesUnsubscribe = onSnapshot(messagesQuery,
        (snapshot) => {
          const messagesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setMessages(messagesData);
        },
        (error) => {
          console.error('Error loading messages:', error);
        }
      );

      // Load NIL U orders
      const ordersQuery = query(collection(db, 'nil-u-orders'), orderBy('createdAt', 'desc'));
      const ordersUnsubscribe = onSnapshot(ordersQuery,
        (snapshot) => {
          const ordersData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setOrders(ordersData);
        },
        (error) => {
          console.error('Error loading orders:', error);
        }
      );

      return () => {
        eventsUnsubscribe();
        servicesUnsubscribe();
        messagesUnsubscribe();
        ordersUnsubscribe();
      };
    } catch (error) {
      console.error('Error setting up listeners:', error);
      setError('Unable to connect to database. Please check Firestore configuration.');
      setLoading(false);
    }
  }, []);

  const handleDateClick = (arg) => {
    setNewEvent({ 
      ...newEvent, 
      start: arg.dateStr, 
      end: arg.dateStr,
      title: '',
      description: '',
      trainer: ''
    });
    setShowEventModal(true);
  };

  const handleEventDrop = async (info) => {
    try {
      const eventRef = doc(db, 'events', info.event.id);
      await updateDoc(eventRef, {
        start: info.event.start.toISOString(),
        end: info.event.end ? info.event.end.toISOString() : info.event.start.toISOString(),
        updatedAt: new Date().toISOString(),
        updatedBy: user.name
      });
    } catch (error) {
      console.error('Error updating event:', error);
      setError('Unable to move event. Please try again.');
      info.revert(); // Revert the change if update fails
    }
  };

  const handleEventResize = async (info) => {
    try {
      const eventRef = doc(db, 'events', info.event.id);
      await updateDoc(eventRef, {
        start: info.event.start.toISOString(),
        end: info.event.end ? info.event.end.toISOString() : info.event.start.toISOString(),
        updatedAt: new Date().toISOString(),
        updatedBy: user.name
      });
    } catch (error) {
      console.error('Error resizing event:', error);
      setError('Unable to resize event. Please try again.');
      info.revert(); // Revert the change if update fails
    }
  };

  const handleEventClick = (info) => {
    const event = info.event;
    setNewEvent({
      title: event.title,
      start: event.start.toISOString().slice(0, 16),
      end: event.end ? event.end.toISOString().slice(0, 16) : event.start.toISOString().slice(0, 16),
      description: event.extendedProps.description || '',
      trainer: event.extendedProps.trainer || ''
    });
    setShowEventModal(true);
  };

  const handleSelectRange = (info) => {
    setNewEvent({
      title: '',
      start: info.startStr,
      end: info.endStr,
      description: '',
      trainer: ''
    });
    setShowEventModal(true);
  };

  const handleEventAdd = async () => {
    if (!newEvent.title || !newEvent.start) return;

    try {
      await addDoc(collection(db, 'events'), {
        ...newEvent,
        createdBy: user.name,
        createdAt: new Date().toISOString()
      });
      setShowEventModal(false);
      setNewEvent({ title: '', start: '', end: '', description: '', trainer: '' });
    } catch (error) {
      console.error('Error adding event:', error);
      setError('Unable to add event. Please check Firestore rules.');
    }
  };

  const handleServiceAdd = async () => {
    if (!newService.name || !newService.description) return;

    try {
      await addDoc(collection(db, 'services'), {
        ...newService,
        createdBy: user.name,
        createdAt: new Date().toISOString()
      });
      setNewService({ name: '', description: '', duration: '', trainer: '' });
    } catch (error) {
      console.error('Error adding service:', error);
      setError('Unable to add service. Please check Firestore rules.');
    }
  };

  const handleMessageSend = async () => {
    if (!newMessage.trim()) return;

    try {
      await addDoc(collection(db, 'messages'), {
        text: newMessage,
        sender: user.name,
        senderRole: user.role,
        createdAt: new Date().toISOString()
      });
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Unable to send message. Please check Firestore rules.');
    }
  };

  const handleServiceDelete = async (serviceId) => {
    try {
      await deleteDoc(doc(db, 'services', serviceId));
    } catch (error) {
      console.error('Error deleting service:', error);
      setError('Unable to delete service. Please check Firestore rules.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-spi-600 mx-auto shadow-lg"></div>
          <p className="mt-6 text-lg font-medium text-gray-700">Loading your workspace...</p>
          <p className="mt-2 text-sm text-gray-500">Setting up your performance dashboard</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Premium Header */}
      <div className="bg-white shadow-lg border-b border-gray-100 sticky top-0 z-50 backdrop-blur-sm bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="w-12 h-12 bg-spi-600 text-white rounded-xl flex items-center justify-center font-bold text-lg shadow-lg border-4 border-spi-100">
                  {user.initials}
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Performance Hub
                </h1>
                <div className="flex items-center space-x-2 mt-1">
                  <p className="text-sm font-medium text-gray-700">{user.name}</p>
                  <span className="text-gray-300">•</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-spi-100 text-spi-800">
                    {user.role}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="group flex items-center space-x-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl hover:shadow-md transition-all duration-200 font-medium border border-gray-200"
            >
              <svg className="w-4 h-4 group-hover:rotate-12 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error Banner - Enhanced */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 shadow-sm">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.502 0L4.35 19.5c-.77.833-.208 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-semibold text-red-800">Connection Issue</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <p className="text-xs text-red-600 mt-2 opacity-80">
                Database connectivity restored automatically
              </p>
            </div>
            <button 
              onClick={() => setError('')}
              className="ml-auto pl-3"
            >
              <svg className="h-5 w-5 text-red-400 hover:text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Premium Navigation */}
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-1">
            <button
              onClick={() => setActiveTab('calendar')}
              className={`group relative py-4 px-6 font-semibold text-sm flex items-center space-x-3 transition-all duration-200 rounded-t-lg ${
                activeTab === 'calendar'
                  ? 'text-spi-600 bg-spi-50 border-b-2 border-spi-500'
                  : 'text-gray-600 hover:text-spi-600 hover:bg-gray-50'
              }`}
            >
              <div className={`p-2 rounded-lg transition-colors ${
                activeTab === 'calendar' ? 'bg-spi-100' : 'bg-gray-100 group-hover:bg-spi-50'
              }`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <span>Training Calendar</span>
              {events.length > 0 && (
                <span className="bg-spi-500 text-white text-xs rounded-full px-2 py-1 font-bold">
                  {events.length}
                </span>
              )}
            </button>
            
            <button
              onClick={() => setActiveTab('services')}
              className={`group relative py-4 px-6 font-semibold text-sm flex items-center space-x-3 transition-all duration-200 rounded-t-lg ${
                activeTab === 'services'
                  ? 'text-spi-600 bg-spi-50 border-b-2 border-spi-500'
                  : 'text-gray-600 hover:text-spi-600 hover:bg-gray-50'
              }`}
            >
              <div className={`p-2 rounded-lg transition-colors ${
                activeTab === 'services' ? 'bg-spi-100' : 'bg-gray-100 group-hover:bg-spi-50'
              }`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <span>Performance Services</span>
              {services.length > 0 && (
                <span className="bg-spi-500 text-white text-xs rounded-full px-2 py-1 font-bold">
                  {services.length}
                </span>
              )}
            </button>
            
            <button
              onClick={() => setActiveTab('messages')}
              className={`group relative py-4 px-6 font-semibold text-sm flex items-center space-x-3 transition-all duration-200 rounded-t-lg ${
                activeTab === 'messages'
                  ? 'text-spi-600 bg-spi-50 border-b-2 border-spi-500'
                  : 'text-gray-600 hover:text-spi-600 hover:bg-gray-50'
              }`}
            >
              <div className={`relative p-2 rounded-lg transition-colors ${
                activeTab === 'messages' ? 'bg-spi-100' : 'bg-gray-100 group-hover:bg-spi-50'
              }`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {messages.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {messages.length > 9 ? '9+' : messages.length}
                  </span>
                )}
              </div>
              <span>Team Hub</span>
            </button>

            <button
              onClick={() => setActiveTab('orders')}
              className={`group relative py-4 px-6 font-semibold text-sm flex items-center space-x-3 transition-all duration-200 rounded-t-lg ${
                activeTab === 'orders'
                  ? 'text-spi-600 bg-spi-50 border-b-2 border-spi-500'
                  : 'text-gray-600 hover:text-spi-600 hover:bg-gray-50'
              }`}
            >
              <div className={`relative p-2 rounded-lg transition-colors ${
                activeTab === 'orders' ? 'bg-spi-100' : 'bg-gray-100 group-hover:bg-spi-50'
              }`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
                {orders.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {orders.length > 9 ? '9+' : orders.length}
                  </span>
                )}
              </div>
              <span>NIL U Orders</span>
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className="space-y-12">
            {/* Simple Header */}
            <div className="space-y-6">
              <div>
                <h1 className="text-4xl font-light text-gray-900 tracking-tight">Training Calendar</h1>
                <p className="text-lg text-gray-600 mt-2 font-light">Manage your performance schedule</p>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span>{events.length} scheduled sessions</span>
                  <span>•</span>
                  <span>Drag to reschedule</span>
                </div>
                <button
                  onClick={() => setShowEventModal(true)}
                  className="px-6 py-2.5 bg-spi-600 hover:bg-spi-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
                >
                  Add Session
                </button>
              </div>
            </div>

            {/* Calendar Container */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-8 border-b border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-2 h-2 bg-spi-600 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700">Program Schedule</span>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <style jsx>{`
                  .fc {
                    font-family: inherit;
                    font-weight: 300;
                  }
                  .fc-toolbar {
                    margin-bottom: 1.5rem !important;
                    padding: 0 !important;
                  }
                  .fc-toolbar-chunk {
                    display: flex !important;
                    align-items: center !important;
                    gap: 0.5rem !important;
                  }
                  .fc-toolbar-title {
                    font-size: 1.375rem !important;
                    font-weight: 400 !important;
                    color: #1f2937 !important;
                    margin: 0 !important;
                  }
                  .fc-button-group {
                    display: inline-flex !important;
                    border-radius: 6px !important;
                    overflow: hidden !important;
                    border: 1px solid #d1d5db !important;
                  }
                  .fc-button {
                    background: white !important;
                    color: #374151 !important;
                    border: none !important;
                    border-right: 1px solid #d1d5db !important;
                    border-radius: 0 !important;
                    font-weight: 500 !important;
                    font-size: 0.875rem !important;
                    padding: 0.5rem 0.875rem !important;
                    transition: all 0.15s ease !important;
                    box-shadow: none !important;
                    text-transform: none !important;
                    min-width: auto !important;
                  }
                  .fc-button:last-child {
                    border-right: none !important;
                  }
                  .fc-button:hover {
                    background: #f8fafc !important;
                    color: #1f2937 !important;
                  }
                  .fc-button:focus {
                    box-shadow: inset 0 0 0 2px #3b82f6 !important;
                    outline: none !important;
                    z-index: 1 !important;
                  }
                  .fc-button-active {
                    background: #3b82f6 !important;
                    color: white !important;
                  }
                  .fc-button-active:hover {
                    background: #2563eb !important;
                  }
                  .fc-prev-button, .fc-next-button {
                    background: white !important;
                    border: 1px solid #d1d5db !important;
                    border-radius: 6px !important;
                    color: #6b7280 !important;
                    padding: 0.5rem !important;
                    margin: 0 0.25rem !important;
                    width: 2rem !important;
                    height: 2rem !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                  }
                  .fc-prev-button:hover, .fc-next-button:hover {
                    background: #f8fafc !important;
                    border-color: #9ca3af !important;
                    color: #374151 !important;
                  }
                  .fc-today-button {
                    background: white !important;
                    color: #374151 !important;
                    border: 1px solid #d1d5db !important;
                    border-radius: 6px !important;
                    font-weight: 500 !important;
                    font-size: 0.875rem !important;
                    padding: 0.5rem 0.875rem !important;
                    margin-left: 0.75rem !important;
                  }
                  .fc-today-button:hover {
                    background: #f8fafc !important;
                    border-color: #9ca3af !important;
                  }
                  .fc-event {
                    background: #0284c7 !important;
                    border: none !important;
                    border-radius: 4px !important;
                    font-size: 0.75rem !important;
                    font-weight: 500 !important;
                    padding: 2px 6px !important;
                    cursor: pointer !important;
                  }
                  .fc-event:hover {
                    background: #0369a1 !important;
                  }
                  .fc-day-today {
                    background: #f0f9ff !important;
                  }
                  .fc-col-header-cell {
                    background: white !important;
                    font-weight: 500 !important;
                    font-size: 0.75rem !important;
                    color: #6b7280 !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.05em !important;
                    padding: 1rem 0.5rem !important;
                    border-bottom: 1px solid #f3f4f6 !important;
                  }
                  .fc-scrollgrid {
                    border: 1px solid #f3f4f6 !important;
                    border-radius: 8px !important;
                  }
                  .fc-scrollgrid-sync-table {
                    border-spacing: 0 !important;
                  }
                  .fc-daygrid-day {
                    border-color: #f9fafb !important;
                    min-height: 4rem !important;
                  }
                  .fc-daygrid-day:hover {
                    background-color: #fafbfc !important;
                  }
                  .fc-daygrid-day-number {
                    font-size: 0.875rem !important;
                    font-weight: 400 !important;
                    color: #374151 !important;
                    padding: 0.5rem !important;
                  }
                  .fc-timegrid-slot {
                    border-color: #f9fafb !important;
                    height: 3rem !important;
                  }
                  .fc-timegrid-axis-cushion {
                    font-size: 0.75rem !important;
                    color: #9ca3af !important;
                    font-weight: 400 !important;
                  }
                  .fc-list-empty {
                    background: white !important;
                    color: #6b7280 !important;
                    font-weight: 400 !important;
                  }
                `}</style>
                <div className="calendar-container bg-white">
                  <FullCalendar
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                    initialView="dayGridMonth"
                    headerToolbar={{
                      left: 'prev,next today',
                      center: 'title',
                      right: 'dayGridMonth timeGridWeek listWeek'
                    }}
                    buttonText={{
                      today: 'Today',
                      month: 'Month',
                      week: 'Week',
                      list: 'Schedule'
                    }}
                    events={events}
                    
                    // Interaction handlers
                    dateClick={handleDateClick}
                    eventClick={handleEventClick}
                    eventDrop={handleEventDrop}
                    eventResize={handleEventResize}
                    select={handleSelectRange}
                    
                    // Enable interactions
                    editable={true}
                    droppable={true}
                    selectable={true}
                    selectMirror={true}
                    dayMaxEvents={3}
                    
                    // Visual settings
                    height="600px"
                    eventColor="#0284c7"
                    eventTextColor="#ffffff"
                    
                    // Responsive settings
                    aspectRatio={1.6}
                    handleWindowResize={true}
                    
                    // Time settings
                    slotMinTime="06:00:00"
                    slotMaxTime="22:00:00"
                    slotDuration="01:00:00"
                    snapDuration="00:30:00"
                    
                    // Event rendering - simplified
                    eventContent={(arg) => (
                      <div className="p-1">
                        <div className="font-medium text-xs truncate">{arg.event.title}</div>
                        {arg.event.extendedProps.trainer && (
                          <div className="text-xs opacity-75 truncate">{arg.event.extendedProps.trainer}</div>
                        )}
                      </div>
                    )}
                    
                    // Selection settings
                    selectConstraint={{
                      start: '06:00',
                      end: '22:00'
                    }}
                    
                    // Event constraints
                    eventConstraint={{
                      start: '06:00',
                      end: '22:00'
                    }}
                    
                    // Simplified event settings
                    eventOverlap={false}
                    selectOverlap={false}
                    nowIndicator={true}
                  />
                </div>
              </div>

            </div>

            {/* Clean stats section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center py-6">
                <div className="text-2xl font-light text-gray-900">{events.length}</div>
                <div className="text-sm text-gray-500 mt-1">Scheduled Sessions</div>
              </div>
              <div className="text-center py-6">
                <div className="text-2xl font-light text-gray-900">{services.length}</div>
                <div className="text-sm text-gray-500 mt-1">Active Services</div>
              </div>
              <div className="text-center py-6">
                <div className="text-2xl font-light text-gray-900">{orders.filter(o => o.status === 'paid').length}</div>
                <div className="text-sm text-gray-500 mt-1">NIL U Orders</div>
              </div>
            </div>
          </div>
        )}

        {/* Event Modal */}
        {showEventModal && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-light text-gray-900">Schedule New Event</h3>
                  <button
                    onClick={() => setShowEventModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                  
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Event Title</label>
                    <input
                      type="text"
                      value={newEvent.title}
                      onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spi-500 focus:border-spi-500 transition-colors"
                      placeholder="e.g., Speed Training Session"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Date & Time</label>
                      <input
                        type="datetime-local"
                        value={newEvent.start}
                        onChange={(e) => setNewEvent({...newEvent, start: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spi-500 focus:border-spi-500 transition-colors"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End Date & Time</label>
                      <input
                        type="datetime-local"
                        value={newEvent.end}
                        onChange={(e) => setNewEvent({...newEvent, end: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spi-500 focus:border-spi-500 transition-colors"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Trainer/Instructor</label>
                    <select
                      value={newEvent.trainer}
                      onChange={(e) => setNewEvent({...newEvent, trainer: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spi-500 focus:border-spi-500 transition-colors"
                    >
                      <option value="">Select Trainer</option>
                      <option value="Justin King">Justin King</option>
                      <option value="Michael Susi">Michael Susi</option>
                      <option value="Team Beast Mode">Team Beast Mode</option>
                      <option value="Dr. Monos">Dr. Monos</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
                    <textarea
                      value={newEvent.description}
                      onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spi-500 focus:border-spi-500 transition-colors"
                      rows="3"
                      placeholder="Additional details about this event..."
                    />
                  </div>
                </div>
                
                <div className="mt-8 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowEventModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEventAdd}
                    className="px-6 py-2 bg-spi-600 text-white rounded-lg hover:bg-spi-700 transition-colors font-medium"
                  >
                    Create Event
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="mb-6">
                <h2 className="text-2xl font-light text-gray-900 mb-2">Services Management</h2>
                <p className="text-gray-600">Manage training services and program offerings</p>
              </div>
              
              {/* Add Service Form */}
              <div className="mb-8 p-6 bg-gray-50 rounded-lg border">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Service</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Service Name</label>
                    <input
                      type="text"
                      value={newService.name}
                      onChange={(e) => setNewService({...newService, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spi-500 focus:border-spi-500 transition-colors"
                      placeholder="e.g., Speed Training"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assigned Trainer</label>
                    <select
                      value={newService.trainer}
                      onChange={(e) => setNewService({...newService, trainer: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spi-500 focus:border-spi-500 transition-colors"
                    >
                      <option value="">Select Trainer</option>
                      <option value="Justin King">Justin King</option>
                      <option value="Michael Susi">Michael Susi</option>
                      <option value="Team Beast Mode">Team Beast Mode</option>
                      <option value="Dr. Monos">Dr. Monos</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
                    <input
                      type="text"
                      value={newService.duration}
                      onChange={(e) => setNewService({...newService, duration: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spi-500 focus:border-spi-500 transition-colors"
                      placeholder="e.g., 60 minutes"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={newService.description}
                      onChange={(e) => setNewService({...newService, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spi-500 focus:border-spi-500 transition-colors"
                      rows="3"
                      placeholder="Detailed description of the service..."
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <button
                    onClick={handleServiceAdd}
                    className="px-6 py-2 bg-spi-600 text-white rounded-lg hover:bg-spi-700 transition-colors font-medium"
                  >
                    Create Service
                  </button>
                </div>
              </div>

              {/* Services List */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Current Services</h3>
                {services.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No services created yet. Add your first service above.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {services.map((service) => (
                      <div key={service.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="font-semibold text-lg text-gray-900">{service.name}</h4>
                          <button
                            onClick={() => handleServiceDelete(service.id)}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center text-gray-600">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span>{service.trainer || 'No trainer assigned'}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{service.duration || 'Duration not specified'}</span>
                          </div>
                        </div>
                        <p className="mt-3 text-gray-700 text-sm leading-relaxed">{service.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            {/* Revenue Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-sm text-gray-500 mb-1">Total Orders</p>
                <p className="text-2xl font-light text-gray-900">{orders.length}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-sm text-gray-500 mb-1">Paid Orders</p>
                <p className="text-2xl font-light text-green-600">{orders.filter(o => o.status === 'paid').length}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-sm text-gray-500 mb-1">Total Seats Sold</p>
                <p className="text-2xl font-light text-gray-900">
                  {orders.filter(o => o.status === 'paid').reduce((sum, o) => sum + (o.seats || 0), 0)}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-sm text-gray-500 mb-1">Total Revenue</p>
                <p className="text-2xl font-light text-gray-900">
                  ${orders.filter(o => o.status === 'paid').reduce((sum, o) => sum + (o.total || 0), 0).toFixed(2)}
                </p>
              </div>
            </div>

            {/* Orders List */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-2xl font-light text-gray-900">NIL U Orders</h2>
                <p className="text-gray-500 text-sm mt-1">Click an order to view student emails</p>
              </div>

              {orders.length === 0 ? (
                <div className="text-center py-16 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <p>No orders yet. Orders will appear here once purchases are made.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {orders.map((order) => (
                    <div key={order.id} className="hover:bg-gray-50 transition-colors">
                      <button
                        onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                        className="w-full text-left p-6"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                              order.status === 'paid' ? 'bg-green-500' : 'bg-yellow-500'
                            }`} />
                            <div>
                              <p className="font-semibold text-gray-900">{order.organization || 'Unknown Org'}</p>
                              <p className="text-sm text-gray-500">{order.purchaserName} ({order.purchaserEmail})</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <p className="font-medium text-gray-900">{order.seats} seat{order.seats !== 1 ? 's' : ''}</p>
                              <p className="text-sm text-gray-500">${order.total?.toFixed(2)}</p>
                            </div>
                            <div className="text-right hidden sm:block">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                order.status === 'paid'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {order.status === 'paid' ? 'Paid' : 'Pending'}
                              </span>
                              <p className="text-xs text-gray-400 mt-1">
                                {order.createdAt?.toDate ? order.createdAt.toDate().toLocaleDateString() : 'N/A'}
                              </p>
                            </div>
                            <svg className={`w-5 h-5 text-gray-400 transition-transform ${expandedOrder === order.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </button>

                      {expandedOrder === order.id && (
                        <div className="px-6 pb-6">
                          <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                              <div>
                                <p className="text-xs text-gray-500">Tier</p>
                                <p className="text-sm font-medium text-gray-900">{order.tierName}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Price/Seat</p>
                                <p className="text-sm font-medium text-gray-900">${order.pricePerSeat}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Order ID</p>
                                <p className="text-sm font-mono text-gray-600 truncate">{order.id}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Stripe Session</p>
                                <p className="text-sm font-mono text-gray-600 truncate">{order.stripeSessionId || 'N/A'}</p>
                              </div>
                            </div>

                            <div>
                              <div className="flex items-center justify-between mb-3">
                                <p className="text-sm font-semibold text-gray-900">
                                  Student Emails ({order.studentEmails?.length || 0})
                                </p>
                                {order.studentEmails?.length > 0 && (
                                  <button
                                    onClick={() => {
                                      const text = order.studentEmails.join('\n');
                                      navigator.clipboard.writeText(text);
                                    }}
                                    className="text-xs text-spi-600 hover:text-spi-700 font-medium"
                                  >
                                    Copy All
                                  </button>
                                )}
                              </div>
                              {order.studentEmails?.length > 0 ? (
                                <div className="bg-white rounded-lg border border-gray-200 p-3 max-h-48 overflow-y-auto">
                                  <div className="space-y-1">
                                    {order.studentEmails.map((email, i) => (
                                      <p key={i} className="text-sm font-mono text-gray-700">{email}</p>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <p className="text-sm text-gray-400 italic">No emails provided</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-light text-gray-900 mb-2">Team Communication</h2>
              <p className="text-gray-600">Collaborate and coordinate with team members</p>
            </div>
            
            {/* Message List */}
            <div className="h-96 overflow-y-auto p-6 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div key={message.id} className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-spi-600 text-white rounded-lg flex items-center justify-center text-sm font-medium">
                          {message.sender.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </div>
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900">{message.sender}</p>
                          <span className="text-xs text-gray-400">•</span>
                          <p className="text-xs text-gray-500">{message.senderRole}</p>
                        </div>
                        <p className="text-sm text-gray-700 mt-1 leading-relaxed">{message.text}</p>
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(message.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Message Input */}
            <div className="p-6 border-t bg-gray-50">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleMessageSend()}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spi-500 focus:border-spi-500 transition-colors"
                  placeholder="Type your message..."
                />
                <button
                  onClick={handleMessageSend}
                  disabled={!newMessage.trim()}
                  className="px-6 py-2 bg-spi-600 text-white rounded-lg hover:bg-spi-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;